import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_elite-fitness-jorge/artifacts/xdzfftfj_02_about_sonriendo_gym.jpg";

export default function About() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 relative aspect-[4/5] overflow-hidden zoom-wrap"
        >
          <motion.img
            src={ABOUT_IMG}
            alt="Jorge Aguilar"
            style={{ y }}
            className="absolute inset-0 w-full h-[120%] object-cover zoom-img"
          />
          <div className="absolute inset-0 ring-1 ring-white/10" />
          <div className="absolute -bottom-1 left-0 right-0 h-1/3 bg-gradient-to-t from-black to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
            <div className="font-display text-2xl tracking-wide">JORGE A.</div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#FF6B00]">PDC, MX</div>
          </div>
        </motion.div>

        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-xs tracking-[0.4em] text-[#FF6B00] mb-5 uppercase"
          >
            {t.about.eyebrow}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] uppercase whitespace-pre-line"
            data-testid="about-title"
          >
            {t.about.title}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 space-y-5 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-2xl"
          >
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
          </motion.div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
            {[
              { v: t.about.stat1, l: t.about.stat1Label },
              { v: t.about.stat2, l: t.about.stat2Label },
              { v: t.about.stat3, l: t.about.stat3Label },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                className="border-l-2 border-[#FF6B00] pl-4"
              >
                <div className="font-display text-3xl sm:text-4xl">{s.v}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
